import IconInfo from '@apollo/icons/default/IconInfo.svg';
import { Alert, Button } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useContext, useEffect } from 'react';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';

import { FlowTypeContext } from '../FlowTypeProvider';
import {
  ButtonState,
  NewSupergraphOnboardingControls,
} from '../NewSupergraphOnboardingControls';
import { NewSupergraphOnboardingStepLayout } from '../NewSupergraphOnboardingStepLayout';
import {
  CommonCloudDedicatedProps,
  CommonStepProps,
  SupergraphOnboardingStep,
} from '../shared';

import { RegisteredGraphTabContent } from './RegisteredGraphTabContent';
import { UnregisteredGraphTabContent } from './UnregisteredGraphTabContent';

export const YourGraphQLAPIStep = ({
  setPreviousStep,
  previousStep,
  values,
  ...contentProps
}: CommonCloudDedicatedProps &
  CommonStepProps &
  React.ComponentProps<typeof YourGraphQLAPIStepContent> &
  Omit<
    React.ComponentProps<typeof NewSupergraphOnboardingStepLayout>,
    'description' | 'image' | 'children' | 'stepId'
  >) => {
  return (
    <NewSupergraphOnboardingStepLayout
      values={values}
      stepId={SupergraphOnboardingStep.YourGraphQLAPI}
      previousStep={previousStep}
      setPreviousStep={setPreviousStep}
      hasPrivateSubgraphs={contentProps.hasPrivateSubgraphs}
    >
      <YourGraphQLAPIStepContent {...contentProps} values={values} />
    </NewSupergraphOnboardingStepLayout>
  );
};

const YourGraphQLAPIStepContent = ({
  values,
  errors,
  touched,
  variantsToCreateSupergraphFrom,
  setFieldValue,
  shouldSeeTabbedView,
  setValues,
  loading,
  creatingSupergraph,
  continueAnalyticsLabelsByStep,
  whenStepsBegan,
  setWhenStepsBegan,
  setIsUsingLocalIntrospection,
  isUsingLocalIntrospection,
  createSupergraph,
  nextButtonState,
  setNextButtonState,
  createGraphError,
  createGraphValidationError,
  isDedicatedPlan,
  setShouldQueryPrivateSubgraphs,
  ...props
}: {
  shouldSeeTabbedView: boolean;
  nextButtonState: ButtonState;
  loading: boolean;
  setNextButtonState: React.Dispatch<React.SetStateAction<ButtonState>>;
} & CommonCloudDedicatedProps &
  CommonStepProps &
  React.ComponentProps<typeof UnregisteredGraphTabContent> &
  React.ComponentProps<typeof RegisteredGraphTabContent>) => {
  const headerText = isDedicatedPlan
    ? 'Provide your GraphQL API endpoint'
    : 'First, tell us about your GraphQL API';

  const { setFlowType } = useContext(FlowTypeContext);

  useEffect(() => {
    setWhenStepsBegan((prev) => ({
      ...(prev ?? {}),
      [SupergraphOnboardingStep.YourGraphQLAPI]: Date.now(),
    }));
  }, [setWhenStepsBegan]);

  useEffect(() => {
    const registeredTabErrors =
      errors.registeredVariantSubgraphName ||
      errors.registeredEndpointUrl ||
      errors.registeredGraphRef ||
      errors.sdl;

    const unregisteredTabErrors =
      errors.unregisteredSubgraphName ||
      errors.unregisteredEndpointUrl ||
      errors.sdl;

    setNextButtonState({
      isDisabled:
        (values.tab === 'registered' &&
          (values.registeredGraphRef.length < 1 || !!registeredTabErrors)) ||
        (values.tab === 'unregistered' &&
          (values.unregisteredEndpointUrl.length < 1 ||
            !!unregisteredTabErrors)),
      disabledReason:
        values.tab === 'registered'
          ? registeredTabErrors
          : values.tab === 'unregistered'
            ? unregisteredTabErrors
            : undefined,
    });
  }, [
    errors.registeredEndpointUrl,
    errors.registeredGraphRef,
    errors.registeredVariantSubgraphName,
    errors.sdl,
    errors.unregisteredEndpointUrl,
    errors.unregisteredSubgraphName,
    setNextButtonState,
    values.registeredGraphRef.length,
    values.tab,
    values.unregisteredEndpointUrl.length,
  ]);

  return (
    <div className={classnames('mx-auto', isDedicatedPlan && 'w-3/5 md:w-4/5')}>
      {!!(createGraphError?.message || createGraphValidationError) && (
        <ErrorMessage className="mb-2">
          {
            // only show the user-actionable validation errors, otherwise show default error message
          }
          {createGraphValidationError}
        </ErrorMessage>
      )}
      <h2
        className={classnames(
          'text-2xl font-semibold',
          !isDedicatedPlan && 'mb-6',
        )}
      >
        {headerText}
      </h2>
      {isDedicatedPlan && (
        <div className="flex text-sm text-secondary">
          We will automatically provision a router that sits in front of your
          GraphQL API.
          <Tooltip className="w-72" label={tooltipContent} interactive>
            <IconInfo tabIndex={0} data-testid="focusable-info-endpoint-url" />
          </Tooltip>
        </div>
      )}
      <div className="text-base text-secondary">
        {shouldSeeTabbedView && (
          <>
            Select the option that best suits your needs, and we’ll take care of
            the rest!
          </>
        )}
      </div>
      {isDedicatedPlan && (
        <Alert status="info" className="my-6">
          <div className="mb-2 font-semibold">
            How is my data protected with Apollo’s cloud routing?
          </div>
          <div>
            GraphOS platform is SOC 2 Type 2 certified. Apollo Router, the
            underlying technology for cloud routing, has successfully completed
            testing and auditing by Doyensec.
          </div>
        </Alert>
      )}
      {shouldSeeTabbedView && (
        <div className="mb-6 mt-4 border-b border-primary">
          <HeaderTab
            className="ml-0"
            isActive={() => values.tab === 'registered'}
            tabName="Registered Graph"
            onClick={() => setFieldValue('tab', 'registered')}
            tabID="registered"
          />
          <HeaderTab
            isActive={() => values.tab === 'unregistered'}
            tabName="Unregistered Endpoint"
            onClick={() => setFieldValue('tab', 'unregistered')}
            tabID="unregistered"
          />
        </div>
      )}
      {values.tab === 'unregistered' && (
        <UnregisteredGraphTabContent
          errors={errors}
          setFieldValue={setFieldValue}
          touched={touched}
          values={values}
          setValues={setValues}
          isUsingLocalIntrospection={isUsingLocalIntrospection}
          setIsUsingLocalIntrospection={setIsUsingLocalIntrospection}
          {...props}
        />
      )}
      {values.tab === 'registered' && (
        <RegisteredGraphTabContent
          errors={errors}
          setFieldValue={setFieldValue}
          touched={touched}
          values={values}
          variantsToCreateSupergraphFrom={variantsToCreateSupergraphFrom}
          setValues={setValues}
          {...props}
        />
      )}

      {/* New button for Dedicated Alpha Preview customers */}
      {isDedicatedPlan && (
        <div className="mt-8 flex w-full justify-center">
          <Button
            variant="buttonLink"
            onClick={() => {
              setShouldQueryPrivateSubgraphs(true);
              setFlowType('DEDICATED_PRIVATE');
            }}
          >
            My endpoint is not publicly accessible
          </Button>
        </div>
      )}

      <div className="hidden items-center justify-between px-0 py-16 3xl:flex">
        <NewSupergraphOnboardingControls.CancelButton
          creatingSupergraph={creatingSupergraph}
        />
        <NewSupergraphOnboardingControls.NextButton
          whenStepsBegan={whenStepsBegan}
          smallScreen={false}
          creatingSupergraph={creatingSupergraph}
          loading={loading}
          state={nextButtonState}
          continueAnalyticsLabelsByStep={continueAnalyticsLabelsByStep}
        />
      </div>
    </div>
  );
};

// Tooltip for Dedicated Customers (in Alpha preview so not available to the public)
const tooltipContent = (
  <>
    When you create a Cloud Supergraph with Apollo Studio, GraphOS automatically
    provisions and manages a router that sits in front of your GraphQL API.
    Clients send operations to your router's public endpoint instead of sending
    them directly to your API. This way, you can later add more APIs to your
    supergraph and clients can query data from all of them with a single request
    to the router.{' '}
    <ClickableText
      className="underline"
      href="https://www.apollographql.com/docs/graphos/routing/cloud-dedicated/"
      rel="noopener noreferrer"
      target="_blank"
      as="a"
    >
      Read the docs
    </ClickableText>
  </>
);
